import { nanoid } from 'nanoid';
import { z } from 'zod';

import type { W_Material_FindForCompany_VM_Single } from 'ab-api/web/material/findForCompany';

import * as TimeUtils from '@acceligentllc/shared/utils/time';

import type { PlainUnitEnum } from '@acceligentllc/shared/enums/quantityUnit';
import { PlainUnitList, QuantityUnitMap, ConstrainedUnitEnum } from '@acceligentllc/shared/enums/quantityUnit';
import { TimeFormat } from '@acceligentllc/shared/enums/timeFormat';

const unitEnumSchema = z.enum<(PlainUnitEnum | ConstrainedUnitEnum)[]>([...PlainUnitList, ConstrainedUnitEnum.PCT]);

export const MaterialFMBaseCostHistorySchema = z.object({
	validFrom: z.string().refine((_val) => TimeUtils.isDateInCorrectFormat(_val, TimeFormat.DB_DATE_ONLY)),
	baseCost: z.coerce.number<string>().gt(0, 'Base Cost must be greater than 0.'),
	shippingMarkup: z.string().transform((_val) => _val === '' ? null : _val).pipe(z.coerce.number<string>().nonnegative('Shipping markup can not be negative.').nullable()).nullable(),
	salesMarkup: z.string().transform((_val) => _val === '' ? null : _val).pipe(z.coerce.number<string>().nonnegative('Sales Markup can not be negative.').nullable()).nullable(),
	tax: z.string().transform((_val) => _val === '' ? null : _val).pipe(z.coerce.number<string>().nonnegative('Tax can not be negative.').nullable()).nullable(),
	companyShippingMarkup: z.number(),
	companySalesMarkup: z.number(),
	companyTax: z.number(),
	totalCost: z.number(),
});

export const MaterialFMBaseSchema = z.object({
	id: z.string(),
	type: z.union([z.literal('new'), z.literal('existing')]),
	materialId: z.number().nullable(),
	group1: z.string().min(1, 'Group 1 is required'),
	group2: z.string().nullable(),
	group3: z.string().nullable(),
	tags: z.string(),
	itemNumber: z.coerce.number<string>(),
	vendorId: z.number().nullable(),
	vendorUniqueId: z.string().nullable(),
	displayName: z.string().min(2, 'Display Name must be at least 2 characters long.'),
	description: z.string(),
	unit: unitEnumSchema,
	currentRate: MaterialFMBaseCostHistorySchema,
	defaultOnJob: z.boolean(),
});

export const MaterialCreateFMSchema = MaterialFMBaseSchema.extend({
	type: z.literal('new'),
	materialId: z.literal(null),
});

export const MaterialEditFMSchema = MaterialFMBaseSchema.extend({
	type: z.literal('existing'),
	futureRate: MaterialFMBaseCostHistorySchema.nullable(),
	materialId: z.number(),
});

export const MaterialFMSchema = z.discriminatedUnion('type', [
	MaterialCreateFMSchema,
	MaterialEditFMSchema,
]);

const MaterialForDeleteFMSchema = z.object({
	materialId: z.number(),
});

interface VendorKey {
	vendorId: number;
	vendorUniqueId: string;
}

function vendorKey(key: VendorKey) {
	const { vendorId, vendorUniqueId } = key;
	return JSON.stringify([vendorId, vendorUniqueId]);
}

function vendorUniqueIdRefine(items: MaterialFM[], ctx: z.core.$RefinementCtx<MaterialFM[]>) {
	const vendorIdCount: Record<string, number> = {};
	for (const index of items.keys()) {
		const mat = items[index];
		if (!mat.vendorId || !mat.vendorUniqueId) {
			continue;
		}
		const { vendorId, vendorUniqueId } = mat;
		const key = vendorKey({ vendorId, vendorUniqueId });
		if (vendorIdCount[key] >= 1) {
			ctx.addIssue({
				code: 'custom',
				message: `Duplicate Vendor ID: ${vendorUniqueId}`,
				path: [index, 'vendorUniqueId'],
			});
			ctx.addIssue({
				code: 'custom',
				message: `Duplicate Vendor ID: ${vendorUniqueId}`,
				path: [index, 'vendorId'],
			});
		}
		vendorIdCount[key] = (vendorIdCount[key] ?? 0) + 1;
	}
}

export const MaterialFormSchema = z.object({
	materials: z.array(MaterialFMSchema).superRefine(vendorUniqueIdRefine),
	materialsForDelete: z.array(MaterialForDeleteFMSchema),
});
export type MaterialBaseFM = z.infer<typeof MaterialFMBaseSchema>;

export type MaterialEditFM = z.infer<typeof MaterialEditFMSchema>;

export type MaterialEditFMInput = z.input<typeof MaterialEditFMSchema>;

export type MaterialCreateFM = z.infer<typeof MaterialCreateFMSchema>;

export type MaterialCreateFMInput = z.input<typeof MaterialCreateFMSchema>;

export type MaterialFM = z.infer<typeof MaterialFMSchema>;

export type MaterialFMInput = z.input<typeof MaterialFMSchema>;

export type MaterialsFormInput = z.input<typeof MaterialFormSchema>;

export type MaterialsFormOutput = z.infer<typeof MaterialFormSchema>;

export const materialVMtoFMInput = (_mat: W_Material_FindForCompany_VM_Single): MaterialEditFMInput => {
	return {
		id: _mat.id.toString(),
		type: 'existing' as const,
		materialId: _mat.id,
		displayName: _mat.displayName,
		group1: _mat.materialGroups[0] ?? '',
		group2: _mat.materialGroups[1] ?? null,
		group3: _mat.materialGroups[2] ?? null,
		tags: _mat.tags.join(','),
		vendorId: _mat.vendorId,
		vendorUniqueId: _mat.vendorUniqueId,
		itemNumber: _mat.itemNumber.toString(),
		description: _mat.description,
		unit: _mat.unit,
		currentRate: {
			validFrom: _mat.currentRate.validFrom,
			baseCost: _mat.currentRate.baseCost.toString(),
			shippingMarkup: _mat.currentRate.shippingMarkup?.toString() ?? null,
			salesMarkup: _mat.currentRate.salesMarkup?.toString() ?? null,
			tax: _mat.currentRate.tax?.toString() ?? null,
			companySalesMarkup: _mat.currentRate.companySalesMarkup,
			companyShippingMarkup: _mat.currentRate.companyShippingMarkup,
			companyTax: _mat.currentRate.companyTax,
			totalCost: _mat.currentRate.totalCost,
		},
		futureRate: _mat.futureRate ? {
			validFrom: _mat.futureRate.validFrom,
			baseCost: _mat.futureRate.baseCost.toString(),
			shippingMarkup: _mat.futureRate.shippingMarkup?.toString() ?? null,
			salesMarkup: _mat.futureRate.salesMarkup?.toString() ?? null,
			tax: _mat.futureRate.tax?.toString() ?? null,
			companySalesMarkup: _mat.futureRate.companySalesMarkup,
			companyShippingMarkup: _mat.futureRate.companyShippingMarkup,
			companyTax: _mat.futureRate.companyTax,
			totalCost: _mat.futureRate.totalCost,
		} : null,
		defaultOnJob: _mat.defaultOnJob,
	};
};

export const createMaterialsForm = (materials: W_Material_FindForCompany_VM_Single[]): MaterialsFormInput => {
	return {
		materials: materials.map(materialVMtoFMInput),
		materialsForDelete: [],
	};
};

export const createNewMaterial = (): MaterialCreateFMInput => {
	return {
		id: nanoid(),
		materialId: null,
		type: 'new',
		group1: '',
		group2: null,
		group3: null,
		tags: '',
		itemNumber: '',
		vendorId: null,
		vendorUniqueId: null,
		displayName: '',
		description: '',
		unit: QuantityUnitMap.AC,
		currentRate: {
			validFrom: TimeUtils.formatDate(new Date(), TimeFormat.DB_DATE_ONLY),
			baseCost: '',
			shippingMarkup: null,
			salesMarkup: null,
			tax: null,
			companySalesMarkup: 0,
			companyShippingMarkup: 0,
			companyTax: 0,
			totalCost: 0,
		},
		defaultOnJob: false,
	};
};
